import {
  ExclamationTriangleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { FC, useMemo, useState } from "react";
import { classNames, wrapClick } from "utils";

import { FormikProps } from "formik";
import _ from "lodash";

export interface ReopenOfferFormProps {
  form: FormikProps<any>;
}

const formatPercent = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(4)}%` : "N/A";
};

const ReopenOfferForm: FC<ReopenOfferFormProps> = ({ form }) => {
  // New state to track if a participant has been selected for editing
  const [hasBegunEditing, setHasBegunEditing] = useState(false);

  // Normalize participants array once
  const participants = useMemo(
    () =>
      Array.isArray(_.get(form.values, "participants"))
        ? _.get(form.values, "participants")
        : [],
    [form.values]
  );

  // Make only the clicked participant editable; mark others readonly
  const editParticipant = (field: string, id: number) => {
    // Set the state to true, indicating an edit has started
    setHasBegunEditing(true);

    const current = Array.isArray(_.get(form.values, field))
      ? _.get(form.values, field)
      : [];
    const updated = current.map((p: any, idx: number) => ({
      ...p,
      readonly: idx !== id,
    }));
    form.setFieldValue(field, updated);
  };

  return (
    <div className="space-y-6">
      {/* Offer Information Section */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-3">
          Offer Details
        </h3>
        <dl className="space-y-2">
          <div className="flex justify-between items-center">
            <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Facultative Offer Proposed:
            </dt>
            <dd className="text-sm font-semibold text-primary-600 dark:text-primary-400">
              {formatPercent(_.get(form.values, "offered"))}
            </dd>
          </div>

          <div className="flex justify-between items-center">
            <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Co-Insurance Share:
            </dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {formatPercent(_.get(form.values, "co_insurance"))}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          You can accept the proposed share or modify just one participant's
          percentage below, up to reopen the offer.
        </p>
      </div>

      <div className="rounded-md bg-yellow-50 p-4">
        <div className="flex">
          <div className="shrink-0">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="size-5 text-yellow-400"
            />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Attention needed
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Participation can only be modified for one participant at a time
                when reopening an offer. the value for other participants will
                remain unchanged. the lowest value you can set is 0.1%.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Participants Table */}
      <div className="col-span-3">
        <div>
          <span className="text-xs font-light">Offer Participants</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-span-3">
              <div className="mt-1 w-full ">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-md">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-left text-sm font-semibold text-gray-900"
                      >
                        Reinsurer
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-left text-sm font-semibold text-gray-900"
                      >
                        Participation (%)
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-sm font-semibold text-gray-900 w-[38px]"
                      ></th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {participants.length === 0 && (
                      <tr>
                        <td colSpan={3} className="p-3 text-sm text-gray-500">
                          No participants available.
                        </td>
                      </tr>
                    )}

                    {participants.map((participant: any, idx: number) => {
                      const id = `participants.${idx}`;
                      const namePath = `${id}.name`;
                      const partPath = `${id}.participation`;

                      const nameError =
                        _.get(form.errors, namePath) &&
                        _.get(form.touched, namePath);
                      const partError =
                        _.get(form.errors, partPath) &&
                        _.get(form.touched, partPath);

                      return (
                        <tr
                          key={participant?.id ?? idx}
                          className="divide-x divide-gray-200"
                        >
                          <td className="p-0">
                            <input
                              id={namePath}
                              name={namePath}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              // disabled={Boolean(participant?.readonly)}
                              disabled={true}
                              value={_.get(form.values, namePath, "")}
                              placeholder="eg. Reinsurer Name"
                              className={classNames(
                                nameError
                                  ? "ring-red-500 border-red-600 border"
                                  : "border-gray-200",
                                "outline-none disabled:opacity-50 relative block w-full sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] px-3"
                              )}
                            />
                          </td>

                          <td className="p-0">
                            <input
                              id={partPath}
                              name={partPath}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              disabled={Boolean(participant?.readonly)}
                              value={_.get(form.values, partPath, "")}
                              placeholder="eg. 10.0000"
                              className={classNames(
                                partError
                                  ? "ring-red-500 border-red-600 border"
                                  : "border-gray-200",
                                "outline-none relative disabled:bg-gray-100 block w-full sm:text-sm placeholder:font-light placeholder:text-xs h-[38px] px-3"
                              )}
                            />
                          </td>

                          <td className="p-0 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right w-[38px] h-[38px] flex items-center justify-center">
                            <button
                              type="button"
                              onClick={wrapClick(() =>
                                editParticipant("participants", idx)
                              )}
                              aria-label={`Edit participant ${idx + 1}`}
                              // Disable the button once an edit has begun
                              disabled={hasBegunEditing}
                              className={classNames(
                                "inline-flex items-center rounded-full border border-transparent p-1 focus:outline-none focus:ring-2 focus:ring-offset-2",
                                // Apply different styles based on the state
                                hasBegunEditing
                                  ? "text-gray-300"
                                  : "text-blue-500 hover:bg-blue-300 hover:text-blue-900 focus:ring-blue-500"
                              )}
                            >
                              <PencilIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReopenOfferForm;
